import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Flame, LayoutDashboard, User, LogOut, ChevronDown, Dumbbell, Wallet, GraduationCap, Route, Menu, BookOpen, Users, Landmark, Utensils, Palette, Scale, Trophy, ShieldCheck, Settings } from "lucide-react";
import { useState } from "react";
import { APP, AUTH } from "../constants/testIds";
import { NotificationBell } from "../components/NotificationBell";
import { NavGroup, NavGroupMobile } from "../components/NavGroup";
import { roleHome } from "../lib/roleHome";
import { useBranding, logoUrl } from "../contexts/BrandingContext";

const navCls = ({ isActive }) =>
  `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
    isActive ? "bg-white/5 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
  }`;

function useNavGroups(user, t) {
  const perms = user?.permissions || [];
  const has = (p) => perms.includes(p);
  const groups = [
    {
      key: "program", label: t("nav.groupProgram"), icon: Dumbbell,
      items: [
        { to: "/programs", icon: Dumbbell, label: t("nav.programs"), testId: APP.navPrograms },
        ...(["MEMBER", "COACH"].includes(user?.role) ? [{ to: "/my-journey", icon: Route, label: t("nav.journey"), testId: APP.navJourney }] : []),
        ...(has("payment.view") ? [{ to: "/admin/payments", icon: Wallet, label: t("nav.payments"), testId: APP.navPayments }] : []),
      ],
    },
    {
      key: "learning", label: t("nav.groupLearning"), icon: BookOpen,
      items: [
        ...(has("education.view") ? [{ to: "/education", icon: BookOpen, label: t("nav.education"), testId: APP.navEducation }] : []),
        ...(has("measurement.view") ? [{ to: "/measurements", icon: Scale, label: t("nav.measurements"), testId: "nav-measurements" }] : []),
      ],
    },
    {
      key: "community", label: t("nav.groupCommunity"), icon: Utensils,
      items: [
        ...(has("food_journal.view") ? [
          { to: "/food-journal", icon: Utensils, label: t("nav.foodJournal"), testId: "nav-food-journal" },
          { to: "/recognition", icon: Trophy, label: t("nav.recognition"), testId: "nav-recognition" },
        ] : []),
        ...(has("food_journal.moderate") ? [{ to: "/admin/journal", icon: ShieldCheck, label: t("nav.moderation"), testId: "nav-journal-moderation" }] : []),
      ],
    },
    {
      key: "settings", label: t("nav.groupSettings"), icon: Settings,
      items: [
        ...(user?.role === "MASTER_ADMIN" ? [{ to: "/admin/users", icon: Users, label: t("nav.users"), testId: "nav-users" }] : []),
        ...(has("coach_candidate.view") ? [{ to: "/admin/coach-candidates", icon: GraduationCap, label: t("nav.coachDev"), testId: APP.navCoachDev }] : []),
        ...(has("payment_settings.view") ? [{ to: "/admin/payment-settings", icon: Landmark, label: t("nav.paymentSettings"), testId: "nav-payment-settings" }] : []),
        ...(has("branding.edit") ? [{ to: "/admin/branding", icon: Palette, label: t("nav.branding"), testId: "nav-branding" }] : []),
        { to: "/profile", icon: User, label: t("dashboard.profile"), testId: "nav-profile" },
      ],
    },
  ];
  return groups.filter((g) => g.items.length > 0);
}

const ROLE_STYLES = {
  MASTER_ADMIN: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  CO_ADMIN: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  COACH: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  MEMBER: "bg-orange-500/15 text-orange-300 border-orange-500/30",
};

export function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const { branding } = useBranding();
  const accent = branding?.accent_color || "#FF3B30";
  const navigate = useNavigate();
  const roleClass = ROLE_STYLES[user?.role] || "bg-white/5 text-slate-300 border-white/10";
  const fullName = user?.profile?.full_name || user?.email || "";
  const initial = (fullName || "?").charAt(0).toUpperCase();
  const navGroups = useNavGroups(user, t);
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(navGroups[0]?.key || null);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#090A0F] text-slate-200">
      <header className="border-b border-white/5 bg-[#0B0C13]/90 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to={roleHome(user)} className="flex items-center gap-2.5" data-testid={APP.navBrand}>
              {branding?.logo_file_id ? (
                <img src={logoUrl(branding.logo_file_id)} alt="logo" className="h-9 w-9 rounded-lg object-contain bg-white/5 p-0.5" />
              ) : (
                <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, #E02E24)` }}>
                  <Flame className="h-5 w-5 text-white" strokeWidth={2.5} />
                </span>
              )}
              <div className="leading-tight hidden sm:block">
                <div className="font-[Outfit] font-extrabold text-white tracking-tight text-lg">{branding?.app_name || t("common.appName")}</div>
              </div>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              <NavLink to={roleHome(user)} end data-testid="nav-dashboard" className={navCls}>
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </NavLink>
              {navGroups.map((g) => <NavGroup key={g.key} group={g} />)}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid={APP.mobileMenu} variant="ghost" size="icon" onClick={() => setOpen(!open)} className="md:hidden text-slate-300 hover:bg-white/5 h-9 w-9">
              <Menu className="h-5 w-5" />
            </Button>
            <span
              data-testid={APP.roleBadge}
              className={`hidden sm:inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${roleClass}`}
            >
              {t(`roles.${user?.role}`) !== `roles.${user?.role}` ? t(`roles.${user?.role}`) : user?.role}
            </span>
            <NotificationBell />
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid={APP.userMenu}
                  variant="ghost"
                  className="h-9 gap-2 px-2 text-slate-200 hover:bg-white/5 hover:text-white border border-white/10 rounded-full"
                >
                  <span className="h-7 w-7 rounded-full bg-gradient-to-br from-[#FF3B30] to-[#F59E0B] text-white text-sm font-bold flex items-center justify-center">
                    {initial}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#161923] border-white/10 text-slate-200 min-w-52">
                <DropdownMenuLabel className="text-slate-400 font-normal text-xs">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid={APP.profileLink}
                  onClick={() => navigate("/profile")}
                  className="focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <User className="h-4 w-4 mr-2" /> {t("dashboard.profile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={AUTH.logoutBtn}
                  onClick={handleLogout}
                  className="focus:bg-red-500/10 focus:text-red-300 cursor-pointer text-red-400"
                >
                  <LogOut className="h-4 w-4 mr-2" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {open && (
          <nav data-testid="mobile-nav" className="md:hidden border-t border-white/5 px-4 py-1 bg-[#0B0C13]">
            <NavLink to={roleHome(user)} end data-testid="m-nav-dashboard" onClick={() => setOpen(false)} className={navCls}>
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </NavLink>
            {navGroups.map((g) => (
              <NavGroupMobile key={g.key} group={g} open={openGroup === g.key}
                onToggle={() => setOpenGroup(openGroup === g.key ? null : g.key)}
                onNavigate={() => setOpen(false)} />
            ))}
          </nav>
        )}
      </header>
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
